import React, { useCallback, useMemo } from "react"
import { Icons } from "../Icons/index.js"
import { Tooltip } from "../ToolTip/ToolTip.jsx"

/**
 * Tag variant → CSS class mapping.
 * - filled: Solid background tag
 * - outline: Border-only tag with transparent background
 */
const TagVariants = {
    filled: "tag-filled",
    outline: "tag-outline",
}

/**
 * Tag — Removable chip/tag component.
 *
 * Used in ComboBox for selected items, or standalone for keyword/filter displays.
 * Shows a close icon when `showClose` is true; clicking it fires `onClick`.
 *
 * @param {string}  label     - Tag text
 * @param {string}  Variant   - 'filled' | 'outline' (case-insensitive)
 * @param {boolean} showClose - Show the × close button
 * @param {func}    onClick   - Called when the close button is clicked (note: not the tag itself)
 * @param {string}  href      - When set, the label renders as a link to this URL
 * @param {string}  target    - Anchor target when `href` is set (default '_blank')
 */
export const Tag = ({
    label = "",
    Variant = "filled",
    showClose = false,
    onClick = () => {},
    href = "",
    target = "_blank",
    fontClass = "body-s-semibold",
    customClass = "",
    customStyle = {},
}) => {
    // Normalize variant to lowercase for lookup
    const variantKey = Variant?.toLowerCase();
    const variantClass = TagVariants[variantKey] || "tag-filled";

    const computedClass = useMemo(() =>
        [
            "tag",
            variantClass,
            fontClass,
            customClass,
        ].filter(Boolean).join(" "),
        [variantClass, fontClass, customClass]
    );

    /** Close handler — stops propagation to avoid triggering parent click events */
    const handleClose = useCallback((e) => {
        e.stopPropagation()
        onClick?.()
    }, [onClick]);

    return (
        <div className={computedClass} style={customStyle}>
            {label && (
                <Tooltip
                    tooltipText={label}
                    placement="top"
                    wrapperStyle={{ minWidth: 0, overflow: 'hidden', display: 'block' }}
                >
                    {href ? (
                        <a
                            href={href}
                            target={target}
                            rel="noopener noreferrer"
                            className="tag-label tag-label--link"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {label}
                        </a>
                    ) : (
                        <span className="tag-label">{label}</span>
                    )}
                </Tooltip>
            )}

            {showClose && (
                <span
                    className="tag-close"
                    onClick={handleClose}
                >
                    <Icons name="close" size={16} />
                </span>
            )}

        </div>
    )
}